import { Button } from "@/components/ui/button";
import { MemberService } from "@/gen/omnios/v1/member_connect.js";
import { useAuthStore } from "@/lib/auth";
import { transport } from "@/lib/grpc";
import { createClient } from "@connectrpc/connect";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/invite/$token")({
	component: InviteAcceptPage,
});

const client = createClient(MemberService, transport);

type Phase = "loading" | "success" | "already_member" | "error";

function InviteAcceptPage() {
	const { token } = Route.useParams();
	const { isAuthenticated, login } = useAuthStore();
	const navigate = useNavigate();
	const called = useRef(false);
	const { t } = useTranslation();

	const [phase, setPhase] = useState<Phase>("loading");
	const [errorMsg, setErrorMsg] = useState("");

	useEffect(() => {
		if (!isAuthenticated) return;
		if (called.current) return;
		called.current = true;

		client
			.acceptInviteLink({ token })
			.then(() => {
				setPhase("success");
				setTimeout(() => navigate({ to: "/" }), 1500);
			})
			.catch((err: unknown) => {
				const msg = err instanceof Error ? err.message : "";
				if (msg.includes("already a member")) {
					setPhase("already_member");
				} else {
					setPhase("error");
					setErrorMsg(msg || t("invite_error_default"));
				}
			});
	}, [isAuthenticated, token, navigate, t]);

	if (!isAuthenticated) {
		return (
			<div className="flex min-h-screen items-center justify-center bg-background">
				<div className="mx-auto max-w-sm space-y-4 rounded-lg border p-8 text-center">
					<h1 className="text-lg font-semibold">{t("invite_title")}</h1>
					<p className="text-sm text-muted-foreground">
						{t("invite_login_hint")}
					</p>
					<Button className="w-full" onClick={login}>
						{t("login_action")}
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="flex min-h-screen items-center justify-center bg-background">
			<div className="mx-auto max-w-sm space-y-4 rounded-lg border p-8 text-center">
				{phase === "loading" && (
					<p className="text-sm text-muted-foreground">
						{t("invite_accepting")}
					</p>
				)}

				{phase === "success" && (
					<>
						<h1 className="text-lg font-semibold">{t("invite_welcome")}</h1>
						<p className="text-sm text-muted-foreground">
							{t("invite_joined")}
						</p>
					</>
				)}

				{phase === "already_member" && (
					<>
						<h1 className="text-lg font-semibold">
							{t("invite_already_member")}
						</h1>
						<p className="text-sm text-muted-foreground">
							{t("invite_already_member_hint")}
						</p>
						<Button variant="outline" onClick={() => navigate({ to: "/" })}>
							{t("invite_go")}
						</Button>
					</>
				)}

				{phase === "error" && (
					<>
						<h1 className="text-lg font-semibold text-destructive">
							{t("invite_error")}
						</h1>
						<p className="text-sm text-muted-foreground">{errorMsg}</p>
						<Button variant="outline" onClick={() => navigate({ to: "/" })}>
							{t("go_home")}
						</Button>
					</>
				)}
			</div>
		</div>
	);
}
